import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 *
 * This component handles:
 * - Automatically opening the cart drawer after Add to Cart
 * - Sticky cart summary layout
 * - Installments CTA close-on-click
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    this.addEventListener(
      ThemeEvents.cartSectionRestored,
      this.#handleCartSectionRestored
    );

    /**
     * The restore path can set [open] before this module loads,
     * meaning the theme-drawer:open event may already have fired.
     *
     * Check the attribute manually so sticky state is still
     * calculated correctly.
     */
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    this.removeEventListener(
      ThemeEvents.cartSectionRestored,
      this.#handleCartSectionRestored
    );
  }

  /**
   * Recompute sticky cart summary after the cart section
   * is restored/re-rendered.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => {
      this.#updateStickyState();
    });
  };

  /**
   * Runs whenever the cart drawer opens.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    /**
     * Close cart drawer when Shopify installments CTA
     * is clicked so dialogs don't overlap.
     */
    customElements
      .whenDefined('shopify-payment-terms')
      .then(() => {
        const paymentTerms =
          this.querySelector('shopify-payment-terms');

        const cta =
          paymentTerms?.shadowRoot?.querySelector(
            '#shopify-installments-cta'
          );

        cta?.addEventListener(
          'click',
          () => {
            this.#themeDrawer?.close();
          },
          { once: true }
        );
      });
  };

  /**
   * Handles cart line updates.
   *
   * IMPORTANT:
   * Whenever action === "add", the cart drawer will
   * automatically open after a successful Add to Cart.
   *
   * No `auto-open` attribute is required.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    /**
     * AUTO OPEN CART DRAWER
     *
     * Previously:
     *
     * this.hasAttribute('auto-open') &&
     * event.action === 'add'
     *
     * That meant the drawer only opened when the
     * cart-drawer-component had the auto-open attribute.
     *
     * Now ANY successful Add to Cart can open it.
     */
    const shouldAutoOpen =
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;

    /**
     * Detect if Add to Cart came from inside a modal,
     * for example Shopify Quick Add.
     *
     * We don't want to open the cart drawer on top
     * of another modal.
     */
    const sourceModal =
      /** @type {HTMLDialogElement | null} */ (
        event.target instanceof Element
          ? event.target.closest('dialog:modal')
          : null
      );

    /**
     * NORMAL PRODUCT PAGE / PRODUCT CARD
     *
     * If Add to Cart did not originate from a modal,
     * we can open the cart drawer immediately once
     * cart content exists.
     */
    if (
      shouldAutoOpen &&
      !sourceModal &&
      !this.#isCartEmpty()
    ) {
      this.#themeDrawer?.open();
    }

    /**
     * Wait for Shopify's cart update request to finish.
     */
    event.promise
      ?.then(({ detail }) => {
        const settle = () => {
          requestAnimationFrame(() => {
            this.#updateStickyState();
          });
        };

        /**
         * Don't open drawer if:
         *
         * 1. This wasn't an Add to Cart action
         * 2. Shopify returned an Add to Cart error
         */
        if (
          !shouldAutoOpen ||
          detail?.didError
        ) {
          settle();
          return;
        }

        /**
         * Open the cart drawer after Shopify has
         * successfully updated the cart.
         */
        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open();
          }

          settle();
        };

        /**
         * QUICK ADD / MODAL ADD TO CART
         *
         * If Add to Cart came from a modal,
         * wait for that modal to close first.
         *
         * Then open the cart drawer.
         */
        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            openAndSettle,
            {
              once: true
            }
          );
        } else {
          /**
           * Normal Add to Cart.
           */
          openAndSettle();
        }
      })
      .catch((error) => {
        /**
         * AbortError can occur when a request is
         * intentionally cancelled/replaced.
         */
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Check whether the currently rendered
   * cart drawer is empty.
   *
   * @returns {boolean}
   */
  #isCartEmpty() {
    return Boolean(
      this.querySelector('.cart-drawer--empty')
    );
  }

  /**
   * Determines whether the cart summary should
   * remain sticky.
   */
  #updateStickyState() {
    const dialog = this.#dialog;

    if (!dialog) {
      return;
    }

    /**
     * Refs do not cross nested component boundaries,
     * therefore query directly inside the dialog.
     */
    const content =
      dialog.querySelector(
        '.cart-drawer__content'
      );

    const summary =
      dialog.querySelector(
        '.cart-drawer__summary'
      );

    /**
     * If cart content/summary isn't available,
     * reset sticky state.
     */
    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    /**
     * Compare summary height against total drawer height.
     */
    const drawerHeight =
      dialog.getBoundingClientRect().height;

    const summaryHeight =
      summary.getBoundingClientRect().height;

    /**
     * Prevent division problems in unusual states.
     */
    if (drawerHeight <= 0) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const ratio =
      summaryHeight / drawerHeight;

    /**
     * If summary occupies more than 50% of the drawer,
     * don't make it sticky.
     */
    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

/**
 * Register component only once.
 */
if (
  !customElements.get(
    'cart-drawer-component'
  )
) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}